// This optional code is used to register a service worker.
// register() is not called by default.
//
// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.
//
// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

/**
 * Build a same-origin absolute URL for service-worker.js.
 * This prevents SSRF-style SAST findings by ensuring the URL cannot point to
 * an arbitrary host (we always prefix with window.location.origin).
 */
function getServiceWorkerUrl() {
  const raw = (process.env.PUBLIC_URL || "").trim();

  // If PUBLIC_URL is absolute URL (CDN), do not register SW (must be same-origin)
  if (/^https?:\/\//i.test(raw)) {
    return null;
  }

  // Normalize base path
  let basePath = raw;

  // Ensure basePath starts with "/" if present
  if (basePath && !basePath.startsWith("/")) {
    basePath = `/${basePath}`;
  }

  // Remove trailing slashes
  basePath = basePath.replace(/\/+$/, "");

  // ✅ Return a SAME-ORIGIN PATH ONLY (no scheme/host)
  return `${basePath}/service-worker.js` || "/service-worker.js";
}
// function getServiceWorkerUrl() {
//   // PUBLIC_URL is a build-time constant in CRA builds.
//   // Ensure it becomes a safe path (starts with "/" and has no trailing slash issues).
//   const raw = (process.env.PUBLIC_URL || "").trim();

//   // Normalize to a path-like prefix
//   let basePath = raw;

//   // If PUBLIC_URL is an absolute URL (e.g., CDN), do not register SW because it must be same-origin.
//   // This is consistent with CRA guidance: SW won't work cross-origin.
//   if (/^https?:\/\//i.test(basePath)) {
//     return null;
//   }

//   // Ensure basePath starts with "/" if present
//   if (basePath && !basePath.startsWith("/")) {
//     basePath = `/${basePath}`;
//   }

//   // Remove trailing slashes
//   basePath = basePath.replace(/\/+$/, "");

//   // Construct same-origin absolute URL
//   return `${window.location.origin}${basePath}/service-worker.js`;
// }

export function register(config) {
  if (process.env.NODE_ENV !== "production") return;
  if (!("serviceWorker" in navigator)) return;

  const swUrl = getServiceWorkerUrl();
  if (!swUrl) {
    // If PUBLIC_URL was cross-origin (CDN), skip registration.
    return;
  }

  window.addEventListener("load", () => {
    if (isLocalhost) {
      // This is running on localhost. Let's check if a service worker still exists or not.
      checkValidServiceWorker(swUrl, config);

      // Add some additional logging to localhost, pointing developers to the
      // service worker/PWA documentation.
      navigator.serviceWorker.ready.then(() => {
        // eslint-disable-next-line no-console
        console.log(
          "This web app is being served cache-first by a service worker. " +
            "To learn more, visit https://bit.ly/CRA-PWA"
        );
      });
    } else {
      // Is not localhost. Just register service worker
      registerValidSW(swUrl, config);
    }
  });
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              // eslint-disable-next-line no-console
              console.log(
                "New content is available and will be used when all " +
                  "tabs for this page are closed. See https://bit.ly/CRA-PWA."
              );

              // Execute callback
              if (config && typeof config.onUpdate === "function") {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              // eslint-disable-next-line no-console
              console.log("Content is cached for offline use.");

              // Execute callback
              if (config && typeof config.onSuccess === "function") {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error("Error during service worker registration:", error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  if (!swUrl || typeof swUrl !== "string") return;

  // Must be a single-slash path, not scheme-relative
  if (!swUrl.startsWith("/") || swUrl.startsWith("//")) {
    console.warn("Blocked SW register: invalid path", swUrl);
    return;
  }

  // Optional: strict allowlist (very VA-friendly)
  const allowed = new Set(["/service-worker.js"]);
  if (!allowed.has(swUrl)) {
    console.warn("Blocked SW register: path not allowlisted", swUrl);
    return;
  }

  // ✅ No fetch() -> avoids SSRF/SSRP sink completely
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Keep CRA-style behavior: update and run callback hooks if present
      if (config && typeof config.onSuccess === "function") {
        config.onSuccess(registration);
      }

      // Try updating the SW immediately
      registration.update();

      // If you already have registerValidSW, you can call it here,
      // but ensure registerValidSW doesn't call fetch internally.
      // registerValidSW(swUrl, config);
    })
    .catch(() => {
      // If SW is missing or invalid, unregister and reload (similar effect to old logic)
      navigator.serviceWorker.ready
        .then((registration) => registration.unregister())
        .finally(() => window.location.reload());
    });
}
// function checkValidServiceWorker(swUrl, config) {
//   // Check if the service worker can be found. If it can't, reload the page.
//   fetch(swUrl, { headers: { "Service-Worker": "script" } })
//     .then((response) => {
//       // Ensure service worker exists, and that we really are getting a JS file.
//       const contentType = response.headers.get("content-type");
//       const isJavaScript =
//         contentType && contentType.indexOf("javascript") !== -1;

//       if (response.status === 404 || !isJavaScript) {
//         // No service worker found. Probably a different app. Reload the page.
//         navigator.serviceWorker.ready.then((registration) => {
//           registration.unregister().then(() => {
//             window.location.reload();
//           });
//         });
//       } else {
//         // Service worker found. Proceed as normal.
//         registerValidSW(swUrl, config);
//       }
//     })
//     .catch(() => {
//       // eslint-disable-next-line no-console
//       console.log("No internet connection found. App is running in offline mode.");
//     });
// }

export function unregister() {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.ready
    .then((registration) => {
      registration.unregister();
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error.message);
    });
}